import { useEffect, useState, useCallback, ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { InvitePreview, AcceptInviteResponse } from '@mockd/shared';
import { useAuth } from '../hooks';
import { api, ApiError } from '../utils/apiClient';
import { ROUTES } from '../routes';
import { Alert, Cursor } from '../components/common';

const ROLE_LABEL: Record<string, string> = {
  admin: 'an admin',
  member: 'a member',
  viewer: 'a viewer',
};

function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="card bg-base-100 p-6 text-center">{children}</div>
      </div>
    </div>
  );
}

interface EmailMismatch {
  invitedEmail: string;
  sessionEmail: string;
}

export function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AcceptInviteResponse | null>(null);
  const [acceptMismatch, setAcceptMismatch] = useState<EmailMismatch | null>(null);

  useEffect(() => {
    if (!token) {
      setError('No invite token provided.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data: InvitePreview }>(`/api/invites/${token}`);
        if (!cancelled) setPreview(res.data);
      } catch {
        if (!cancelled) setError('We couldn’t load this invite. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAccept = useCallback(async () => {
    if (!token) return;
    setAccepting(true);
    setError(null);
    try {
      const res = await api.post<{ data: AcceptInviteResponse }>(`/api/invites/${token}/accept`);
      setResult(res.data);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVITE_EMAIL_MISMATCH' && err.details) {
        // Preview was stale (e.g. the account was switched in another tab) — fall
        // back to the same mismatch card using the addresses the server rejected.
        setAcceptMismatch({
          invitedEmail: String(err.details.invitedEmail),
          sessionEmail: String(err.details.sessionEmail),
        });
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to accept invite.');
      }
    } finally {
      setAccepting(false);
    }
  }, [token]);

  const handleSwitchAccount = useCallback(async () => {
    setSwitchingAccount(true);
    try {
      await logout();
    } finally {
      const invitedEmail = acceptMismatch?.invitedEmail ?? preview?.email ?? '';
      const params = new URLSearchParams({ email: invitedEmail, redirect: token ? ROUTES.invite(token) : '/' });
      navigate(`${ROUTES.auth.login()}?${params.toString()}`);
    }
  }, [logout, navigate, acceptMismatch, preview, token]);

  if (loading || authLoading) {
    return (
      <Card>
        <Cursor size="lg" className="mx-auto mb-4" />
        <h1>Loading invite</h1>
      </Card>
    );
  }

  // Successful acceptance
  if (result) {
    return (
      <Card>
        <h1 className="mb-2">
          {result.alreadyMember ? `You’re already on ${result.team.name}` : `Welcome to ${result.team.name}!`}
        </h1>
        <p className="text-base-content/80 mb-6">
          {result.alreadyMember
            ? 'Nothing more to do here.'
            : `You joined as ${ROLE_LABEL[result.role] ?? result.role}.`}
        </p>
        <button className="btn btn-primary w-full" onClick={() => navigate(ROUTES.teams.dashboard(result.team.id))}>
          Go to team
        </button>
      </Card>
    );
  }

  const status = preview?.status ?? 'not_found';

  if (status === 'not_found' || error) {
    return (
      <Card>
        <h1 className="mb-2">Invite not found</h1>
        <p className="text-base-content/80 mb-6">
          {error ?? 'This invite link is invalid or has already been used.'}
        </p>
        <Link to="/dashboard" className="btn btn-primary w-full">Go to dashboard</Link>
      </Card>
    );
  }

  if (status === 'accepted') {
    return (
      <Card>
        <h1 className="mb-2">Invite already used</h1>
        <p className="text-base-content/80 mb-6">This invitation has already been accepted.</p>
        <Link to="/dashboard" className="btn btn-primary w-full">Go to dashboard</Link>
      </Card>
    );
  }

  if (status === 'expired') {
    return (
      <Card>
        <h1 className="mb-2">Invite expired</h1>
        <p className="text-base-content/80 mb-6">
          This invitation to join{preview?.teamName ? ` ${preview.teamName}` : ''} has expired. Ask the
          team owner to send you a new one.
        </p>
        <Link to="/dashboard" className="btn btn-primary w-full">Go to dashboard</Link>
      </Card>
    );
  }

  // status === 'valid'
  const teamName = preview?.teamName ?? 'a team';
  const roleLabel = preview?.role ? ROLE_LABEL[preview.role] ?? `a ${preview.role}` : 'a member';

  // Either the signed-in account doesn't match the invited address (caught before
  // the user can even try), or the accept call was rejected for the same reason
  // (a stale preview — e.g. the account was switched in another tab).
  const preflightMismatch =
    user && preview?.email && normalizeEmail(user.email) !== normalizeEmail(preview.email)
      ? { invitedEmail: preview.email, sessionEmail: user.email }
      : null;
  const mismatch = acceptMismatch ?? preflightMismatch;

  if (mismatch) {
    return (
      <Card>
        <h1 className="mb-2">Wrong account</h1>
        <p className="text-base-content/80 mb-6">
          This invite was sent to <span className="font-medium">{mismatch.invitedEmail}</span>. You're
          signed in as <span className="font-medium">{mismatch.sessionEmail}</span>.
        </p>
        <button className="btn btn-primary w-full mb-3" onClick={handleSwitchAccount} disabled={switchingAccount}>
          {switchingAccount ? 'Signing out…' : `Sign in as ${mismatch.invitedEmail}`}
        </button>
        <Link to="/dashboard" className="btn btn-ghost w-full">Go to dashboard</Link>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="mb-2">Join {teamName} on mockd</h1>
      <p className="text-base-content/80 mb-6">
        You’ve been invited to join <span className="font-medium">{teamName}</span> as {roleLabel}.
      </p>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      {user ? (
        <button className="btn btn-primary w-full" onClick={handleAccept} disabled={accepting}>
          {accepting ? 'Accepting…' : 'Accept invitation'}
        </button>
      ) : (
        <>
          <p className="text-sm text-base-content/70 mb-3">Sign in to accept this invitation.</p>
          {(() => {
            const params = new URLSearchParams({
              ...(preview?.email ? { email: preview.email } : {}),
              redirect: token ? ROUTES.invite(token) : '/',
            });
            const query = params.toString();
            return (
              <>
                <Link to={`${ROUTES.auth.login()}?${query}`} className="btn btn-primary w-full mb-2">
                  Sign in to accept
                </Link>
                <Link to={`${ROUTES.auth.register()}?${query}`} className="btn btn-ghost w-full">
                  New to mockd? Create an account
                </Link>
              </>
            );
          })()}
        </>
      )}
    </Card>
  );
}
